import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Database, RefreshCw, Trash2, AlertTriangle, CheckCircle2,
  Server, HardDrive, Users, FileText, MessageSquare, Shield
} from 'lucide-react';
import { toast } from 'sonner';

export const DatabaseManagement = ({ authFetch, userRole }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [keepUser, setKeepUser] = useState(true);
  
  // Bulk Delete State
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedEnquiries, setSelectedEnquiries] = useState([]);
  const [archivedEnquiries, setArchivedEnquiries] = useState([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  const isDirector = userRole === 'director';

  useEffect(() => {
    fetchStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/admin/database-status');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch database status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (confirmText !== 'RESET-ALL-DATA') {
      toast.error('Please type RESET-ALL-DATA to confirm');
      return;
    }

    setResetting(true);
    try {
      const res = await authFetch(
        `/admin/database-reset?confirmation=${confirmText}&keep_current_user=${keepUser}`,
        { method: 'POST' }
      );

      if (res.ok) {
        await res.json();
        toast.success('Database reset complete');
        setShowResetConfirm(false);
        setConfirmText('');
        fetchStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reset database');
      }
    } catch (err) {
      toast.error('Failed to reset database');
    } finally {
      setResetting(false);
    }
  };
  
  // Fetch archived enquiries for bulk delete
  const fetchArchivedEnquiries = async () => {
    setLoadingEnquiries(true);
    try {
      const res = await authFetch('/enquiries?status=archived');
      if (res.ok) {
        const data = await res.json();
        setArchivedEnquiries(data.enquiries || []);
      }
    } catch (err) {
      console.error('Failed to fetch archived enquiries:', err);
      toast.error('Failed to load archived enquiries');
    } finally {
      setLoadingEnquiries(false);
    }
  };
  
  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (bulkDeleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    if (selectedEnquiries.length === 0) {
      toast.error('No enquiries selected');
      return;
    }
    
    setBulkDeleting(true);
    try {
      const res = await authFetch('/enquiries/bulk/permanent-delete', {
        method: 'POST',
        body: { enquiry_ids: selectedEnquiries }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Successfully deleted ${data.deleted_count} enquiries`);
        setBulkDeleteMode(false);
        setSelectedEnquiries([]);
        setBulkDeleteConfirmText('');
        fetchStatus();
        fetchArchivedEnquiries();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete enquiries');
      }
    } catch (err) {
      toast.error('Failed to delete enquiries');
    } finally {
      setBulkDeleting(false);
    }
  };
  
  // Toggle enquiry selection
  const toggleEnquirySelection = (enquiryId) => {
    setSelectedEnquiries(prev => 
      prev.includes(enquiryId) 
        ? prev.filter(id => id !== enquiryId)
        : [...prev, enquiryId]
    );
  };
  
  // Select all archived enquiries
  const selectAllEnquiries = () => {
    if (selectedEnquiries.length === archivedEnquiries.length) {
      setSelectedEnquiries([]);
    } else {
      setSelectedEnquiries(archivedEnquiries.map(e => e.id));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="database-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center">
            <Database className="w-5 h-5 text-[#1e3a5f]" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-[#1e3a5f]">Database Management</h2>
            <p className="text-slate-500 text-sm">Monitor and manage your database</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStatus}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Server className="w-5 h-5 text-[#1e3a5f]" />
          <h3 className="font-heading font-semibold text-[#1e3a5f]">Connection Status</h3>
        </div>
        
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-3 h-3 rounded-full ${status?.status === 'connected' ? 'bg-emerald-500' : 'bg-red-500'}`} />
          <span className="font-medium text-slate-900">
            {status?.status === 'connected' ? 'Connected' : 'Disconnected'}
          </span>
          <span className="text-slate-500">•</span>
          <span className="text-slate-500 text-sm">Database: {status?.database}</span>
        </div>

        {status?.is_clean && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-emerald-700 text-sm">Database is clean - ready for fresh start</span>
          </div>
        )}
      </div>

      {/* Counts Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Users className="w-5 h-5 text-blue-600 mb-2" />
          <p className="text-2xl font-bold text-[#1e3a5f]">{status?.counts?.users || 0}</p>
          <p className="text-slate-500 text-sm">Users</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <FileText className="w-5 h-5 text-purple-600 mb-2" />
          <p className="text-2xl font-bold text-[#1e3a5f]">{status?.counts?.enquiries || 0}</p>
          <p className="text-slate-500 text-sm">Enquiries</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <MessageSquare className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="text-2xl font-bold text-[#1e3a5f]">{status?.counts?.contacts || 0}</p>
          <p className="text-slate-500 text-sm">Contacts</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <HardDrive className="w-5 h-5 text-orange-600 mb-2" />
          <p className="text-2xl font-bold text-[#1e3a5f]">{status?.counts?.quotes || 0}</p>
          <p className="text-slate-500 text-sm">Quotes</p>
        </div>
      </div>

      {/* Reset Section */}
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trash2 className="w-5 h-5 text-red-600" />
          <h3 className="font-heading font-semibold text-red-600">Reset Database</h3>
        </div>
        
        <p className="text-slate-500 text-sm mb-4">
          Permanently delete all data and start fresh. This is useful when deploying to production for the first time.
        </p>

        {!showResetConfirm ? (
          <Button
            onClick={() => setShowResetConfirm(true)}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Reset Database
          </Button>
        ) : (
          <div className="space-y-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Warning: This action cannot be undone!</p>
                <p className="text-red-700 text-sm mt-1">
                  All enquiries, contacts, quotes, and other data will be permanently deleted.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="keepUser"
                checked={keepUser}
                onChange={(e) => setKeepUser(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="keepUser" className="text-sm text-slate-700">
                Keep my account (recommended)
              </label>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Type <code className="bg-red-100 px-1 rounded">RESET-ALL-DATA</code> to confirm:
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESET-ALL-DATA"
                className="max-w-xs"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowResetConfirm(false);
                  setConfirmText('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReset}
                disabled={confirmText !== 'RESET-ALL-DATA' || resetting}
                variant="destructive"
              >
                {resetting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Confirm Reset
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Environment Info */}
      <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-600">
        <p className="font-medium text-slate-700 mb-2">Environment Notes:</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>Preview and Production use <strong>separate databases</strong> by design</li>
          <li>Use this reset feature in Production to start fresh after deployment</li>
          <li>First user to register after reset becomes Director automatically</li>
        </ul>
      </div>
      
      {/* Bulk Delete Section - Director Only */}
      {isDirector && (
        <div className="bg-white rounded-xl border border-amber-200 p-6" data-testid="bulk-delete-section">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-amber-600" />
            <h3 className="font-heading font-semibold text-amber-800">Bulk Delete Archived Enquiries</h3>
            <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Director Only</span>
          </div>
          
          <p className="text-slate-600 text-sm mb-4">
            Permanently delete multiple archived enquiries at once. This action cannot be undone.
            Only archived enquiries can be bulk deleted.
          </p>
          
          {!bulkDeleteMode ? (
            <Button
              onClick={() => {
                setBulkDeleteMode(true);
                fetchArchivedEnquiries();
              }}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="bulk-delete-start-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Manage Archived Enquiries
            </Button>
          ) : (
            <div className="space-y-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Select enquiries to permanently delete</p>
                  <p className="text-amber-700 text-sm mt-1">
                    Selected enquiries and their associated quotes and decisions will be permanently removed.
                  </p>
                </div>
              </div>
              
              {loadingEnquiries ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 text-amber-500 animate-spin" />
                  <span className="ml-2 text-amber-700">Loading archived enquiries...</span>
                </div>
              ) : archivedEnquiries.length === 0 ? (
                <div className="py-6 text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-slate-600">No archived enquiries to delete</p>
                  <p className="text-slate-500 text-sm">Archive enquiries first before bulk deleting</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between py-2 border-b border-amber-200">
                    <button
                      onClick={selectAllEnquiries}
                      className="text-sm text-amber-700 hover:text-amber-900 font-medium"
                    >
                      {selectedEnquiries.length === archivedEnquiries.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="text-sm text-amber-700">
                      {selectedEnquiries.length} of {archivedEnquiries.length} selected
                    </span>
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {archivedEnquiries.map(enquiry => (
                      <label
                        key={enquiry.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedEnquiries.includes(enquiry.id)
                            ? 'bg-red-50 border-red-200'
                            : 'bg-white border-slate-200 hover:border-amber-300'
                        }`}
                      >
                        <Checkbox
                          checked={selectedEnquiries.includes(enquiry.id)}
                          onCheckedChange={() => toggleEnquirySelection(enquiry.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{enquiry.project_name || 'Unnamed Project'}</p>
                          <p className="text-xs text-slate-500">
                            {enquiry.reference_number} • {enquiry.client_company || 'No client'}
                          </p>
                        </div>
                        {enquiry.is_demo && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Demo</span>
                        )}
                      </label>
                    ))}
                  </div>
                  
                  {selectedEnquiries.length > 0 && (
                    <div className="space-y-3 pt-3 border-t border-amber-200">
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 text-sm font-medium flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Warning: This will permanently delete {selectedEnquiries.length} enquiry{selectedEnquiries.length > 1 ? 'ies' : 'y'}
                        </p>
                        <p className="text-red-700 text-xs mt-1">
                          Associated quotes, decisions, and all related data will also be deleted.
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">
                          Type <code className="bg-red-100 px-1 rounded">DELETE</code> to confirm:
                        </label>
                        <Input
                          value={bulkDeleteConfirmText}
                          onChange={(e) => setBulkDeleteConfirmText(e.target.value.toUpperCase())}
                          placeholder="DELETE"
                          className="max-w-xs"
                          data-testid="bulk-delete-confirm-input"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => {
                    setBulkDeleteMode(false);
                    setSelectedEnquiries([]);
                    setBulkDeleteConfirmText('');
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
                {selectedEnquiries.length > 0 && (
                  <Button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteConfirmText !== 'DELETE' || bulkDeleting}
                    variant="destructive"
                    data-testid="bulk-delete-confirm-btn"
                  >
                    {bulkDeleting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete {selectedEnquiries.length} Enquir{selectedEnquiries.length > 1 ? 'ies' : 'y'}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatabaseManagement;
